import React, { useEffect, useState } from 'react';
import styled from "styled-components";
import MyHeader from "../../../components/common/admin/MyHeader";
import ActionBar from "../../../components/common/admin/ActionBar";
import { addDays, startOfDay, startOfWeek, differenceInWeeks, format, parse, addWeeks } from 'date-fns';
import DailyView from "../../Activities/DailyView";
import WeeklyView from "../../Activities/WeeklyView";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from '../../../contexts/AuthContext';

const Activities = ({isAdminView = true}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { groupInfo } = useAuth();
    const { groupIndex, groups } = location.state || {};
    const daysToShow = 5;
    
    // Get date information from URL parameters
    const searchParams = new URLSearchParams(location.search);
    const dateParam = searchParams.get('date');
    const weekNumParam = searchParams.get('weekNum');
    const viewParam = searchParams.get('view') || 'Day';
    
    const [today, setToday] = useState(() => startOfDay(new Date()));
    
    // If there is a date parameter in the URL, use it; otherwise use course start date or current date
    const [startDate, setStartDate] = useState(() => {
        if (dateParam) {
            try {
                const parsedDate = parse(dateParam, 'yyyy-MM-dd', new Date());
                return parsedDate;
            } catch (error) {
                console.error('Error parsing date from URL:', error);
            }
        }
        
        const todayDate = startOfDay(new Date());
        
        // If no groupInfo, default to today-4
        if (!groupInfo || !groupInfo.startDate) {
            return addDays(todayDate, -daysToShow + 1);
        }
        
        const projectStartDate = startOfDay(groupInfo.startDate);
        const projectEndDate = groupInfo.endDate ? startOfDay(groupInfo.endDate) : null;
        
        // Check if today is within project period
        const isTodayAfterStart = todayDate >= projectStartDate;
        const isTodayBeforeEnd = !projectEndDate || todayDate <= projectEndDate;
        
        // If today is within the project period, show today
        if (isTodayAfterStart && isTodayBeforeEnd) {
            return addDays(todayDate, -daysToShow + 1);
        }
        
        // start from project start date
        return projectStartDate;
    });
    
    // If there is a weekNum parameter in the URL, use it; otherwise calculate the current week
    const [weekNum, setWeekNum] = useState(() => {
        if (weekNumParam && !isNaN(parseInt(weekNumParam))) {
            return parseInt(weekNumParam);
        }
        if (!groupInfo) return 0;
        const groupStartWeek = startOfWeek(groupInfo.startDate, { weekStartsOn: 1 });
        
        let referenceDate = today;
        if (groupInfo.endDate) {
            const projectEndDate = startOfDay(groupInfo.endDate);
            if (today > projectEndDate) {
                return 0;
            }
        }
        
        const referenceWeek = startOfWeek(referenceDate, { weekStartsOn: 1 });
        const weekDifference = differenceInWeeks(referenceWeek, groupStartWeek);
        const pageNum = Math.floor(weekDifference / 5) * 5;
        return Math.max(0, pageNum);
    });
    
    const [selectedMembers, setSelectedMembers] = useState([]);
    const [currentView, setCurrentView] = useState(viewParam);

    // Function to update URL parameters
    const updateUrlParams = (newStartDate, newWeekNum, newView) => {
        const newSearchParams = new URLSearchParams(location.search);
        if (newStartDate) {
            newSearchParams.set('date', format(newStartDate, 'yyyy-MM-dd'));
        }
        if (newWeekNum !== undefined && newWeekNum !== null) {
            newSearchParams.set('weekNum', newWeekNum.toString());
        }
        if (newView) {
            newSearchParams.set('view', newView);
        }
        
        navigate({
            pathname: location.pathname,
            search: newSearchParams.toString()
        }, { 
            state: location.state,
            replace: true 
        });
    };

    const calculateCurrentWeekNum = () => {
        if (!groupInfo) return 0;
        const groupStartWeek = startOfWeek(groupInfo.startDate, { weekStartsOn: 1 });
        
        let referenceDate = today;
        if (groupInfo.endDate) {
            const projectEndDate = startOfDay(groupInfo.endDate);
            if (today > projectEndDate) {
                return 0;
            }
        }
        
        const referenceWeek = startOfWeek(referenceDate, { weekStartsOn: 1 });
        const weekDifference = differenceInWeeks(referenceWeek, groupStartWeek);
        const pageNum = Math.floor(weekDifference / 5) * 5;
        return Math.max(0, pageNum);
    };

    // Handle week click navigation
    const handleWeekClick = (clickedWeekNum) => {
        if (!groupInfo || !groupInfo.startDate) return;
        
        // Calculate the start date of the clicked week (Monday)
        const courseStartWeek = startOfWeek(groupInfo.startDate, { weekStartsOn: 1 });
        const targetWeekStart = addWeeks(courseStartWeek, clickedWeekNum - 1);
        
        // Set the startDate to show the clicked week in Day view
        setStartDate(targetWeekStart);
        
        // Switch to Day view
        setCurrentView('Day');
        
        // Update URL parameters
        updateUrlParams(targetWeekStart, weekNum, 'Day');
    };

    const handleViewChange = (newView) => {
        if (newView === 'Week' && currentView !== 'Week') {
            const newWeekNum = calculateCurrentWeekNum();
            setWeekNum(newWeekNum);
            updateUrlParams(startDate, newWeekNum, newView);
        } else {
            updateUrlParams(startDate, weekNum, newView);
        }
        setCurrentView(newView);
    };

    // Wrap the startDate setter to also update the URL
    const handleStartDateChange = (newStartDate) => {
        setStartDate(newStartDate);
        updateUrlParams(newStartDate, weekNum, currentView);
    };

    // Wrap the weekNum setter to also update the URL
    const handleWeekNumChange = (newWeekNum) => {
        setWeekNum(newWeekNum);
        updateUrlParams(startDate, newWeekNum, currentView);
    };

    useEffect(() => {
        const updateDate = () => {
            const newToday = startOfDay(new Date());
            if (newToday.getTime() !== today.getTime()) {
                setToday(newToday);
                // Do not automatically update startDate, keep the date selected by the user
                if (currentView === 'Week' && !weekNumParam) {
                    // Update weekNum only if there are no URL parameters
                    const newWeekNum = calculateCurrentWeekNum();
                    setWeekNum(newWeekNum);
                    updateUrlParams(startDate, newWeekNum, currentView);
                }
            }
        };
        const intervalId = setInterval(updateDate, 60000);
        return () => clearInterval(intervalId);
    }, [today, daysToShow, currentView, weekNumParam]);

    const goToToday = () => {
        if (currentView === 'Day') {
            let newStartDate;
            // Check if today is within project period
            if (groupInfo && groupInfo.startDate) {
                const projectStartDate = startOfDay(groupInfo.startDate);
                const projectEndDate = groupInfo.endDate ? startOfDay(groupInfo.endDate) : null;
                
                const isTodayAfterStart = today >= projectStartDate;
                const isTodayBeforeEnd = !projectEndDate || today <= projectEndDate;
                
                if (isTodayAfterStart && isTodayBeforeEnd) {
                    newStartDate = addDays(today, -daysToShow + 1);
                } else {
                    newStartDate = projectStartDate;
                }
            } else {
                newStartDate = addDays(today, -daysToShow + 1);
            }
            setStartDate(newStartDate);
            updateUrlParams(newStartDate, weekNum, currentView);
        } else if (currentView === 'Week') {
            const newWeekNum = calculateCurrentWeekNum();
            setWeekNum(newWeekNum);
            updateUrlParams(startDate, newWeekNum, currentView);
        }
    };

    const handleMemberSelect = (member) => {
        setSelectedMembers(prevSelected => {
            if (prevSelected.includes(member)) {
                return prevSelected.filter(m => m !== member);
            } else {
                return [...prevSelected, member];
            }
        });
    };

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        if (searchParams.has('refresh')) {
            searchParams.delete('refresh');
            navigate(location.pathname + '?' + searchParams.toString(), { replace: true });
            window.location.reload();
        }
    }, [location, navigate]);

    return (
        <>
        <MyHeader groupIndex={groupIndex} groups={groups}/>
        <PageWrapper>
            <ActionBar
                weekNum={weekNum}
                setWeekNum={handleWeekNumChange}
                startDate={startDate}
                setStartDate={handleStartDateChange}
                goToToday={goToToday}
                onMemberSelect={handleMemberSelect}
                selectedMembers={selectedMembers}
                currentView={currentView}
                setCurrentView={handleViewChange}
                daysToShow={daysToShow}
            />
            {currentView === 'Day' && (
                <DailyView
                    startDate={startDate}
                    setStartDate={handleStartDateChange}
                    daysToShow={daysToShow}
                    today={today}
                    selectedMembers={selectedMembers}
                    isAdminView={isAdminView}
                />
            )}
            {currentView === 'Week' && (
                <WeeklyView
                    weekNum={weekNum}
                    today={today}
                    selectedMembers={selectedMembers}
                    isAdminView={isAdminView}
                    onWeekClick={handleWeekClick}
                />
            )}
        </PageWrapper>
        </>
    );
};

const PageWrapper = styled.div`
    background-color: #fff;
    display: flex;
    flex-direction: column;
    padding: 15px 20px 80px;
`;

export default Activities;